import { onlineManager } from "@tanstack/react-query";
import {
  logGrazingOfflineReplay,
  replayErrorPayload,
} from "common/utils/grazingOfflineReplayLog";
import { isNetworkRequestError } from "common/utils/networkErrors";

const NETWORK_RETRY_BASE_DELAY_MS = 1000;
const NETWORK_RETRY_MAX_DELAY_MS = 30_000;

let onlineGeneration = 0;
let lastOnline = onlineManager.isOnline();
let unsubscribeOnline: (() => void) | undefined;

function ensureOnlineGenerationTracker(): void {
  if (unsubscribeOnline) return;
  unsubscribeOnline = onlineManager.subscribe((online) => {
    if (!lastOnline && online) {
      onlineGeneration += 1;
      logGrazingOfflineReplay("network.retry.online-generation.increment", {
        onlineGeneration,
      });
    }
    lastOnline = online;
  });
}

export function shouldRetryNetworkMutation(
  failureCount: number,
  error: unknown
): boolean {
  const shouldRetry = isNetworkRequestError(error);
  logGrazingOfflineReplay("network.retry.decision", {
    failureCount,
    shouldRetry,
    ...replayErrorPayload(error),
  });
  return shouldRetry;
}

export function createNetworkMutationRetryDelay(): (
  failureCount: number
) => number {
  ensureOnlineGenerationTracker();
  let generationForDelay = onlineGeneration;
  let resetFailureCount = 0;

  return (failureCount: number) => {
    if (generationForDelay !== onlineGeneration) {
      generationForDelay = onlineGeneration;
      resetFailureCount = failureCount;
    }
    const attemptAfterLastReconnect = Math.max(
      0,
      failureCount - resetFailureCount
    );
    const delayMs = Math.min(
      NETWORK_RETRY_BASE_DELAY_MS * 2 ** attemptAfterLastReconnect,
      NETWORK_RETRY_MAX_DELAY_MS
    );
    logGrazingOfflineReplay("network.retry.delay", {
      failureCount,
      resetFailureCount,
      attemptAfterLastReconnect,
      delayMs,
      onlineGeneration,
    });
    return delayMs;
  };
}

export function resetNetworkMutationRetryForTests(): void {
  unsubscribeOnline?.();
  unsubscribeOnline = undefined;
  onlineGeneration = 0;
  lastOnline = onlineManager.isOnline();
}
